import type {
  NotificationEntityLink,
  NotificationInboxItem,
} from '@/features/notifications/types';

function getActorName(notification: NotificationInboxItem) {
  return notification.event.actor?.display_name ?? 'Someone';
}

function getEntityLabel(entity?: NotificationEntityLink | null) {
  return entity?.label ?? entity?.title ?? entity?.name ?? null;
}

function NotificationLink({
  entity,
  fallback,
}: {
  entity?: NotificationEntityLink | null;
  fallback: string;
}) {
  const label = getEntityLabel(entity) ?? fallback;

  if (!entity?.url) {
    return <span>{label}</span>;
  }

  return <span className="inlineLink">{label}</span>;
}

function NotificationValue({
  children,
  tone = 'info',
}: {
  children: React.ReactNode;
  tone?: 'default' | 'success' | 'info' | 'error' | 'warning';
}) {
  const className =
    tone === 'success'
      ? 'notificationSuccess'
      : tone === 'error'
        ? 'notificationError'
        : tone === 'warning'
          ? 'notificationWarning'
          : tone === 'info'
            ? 'notificationInfo'
            : undefined;

  return <span className={className}>{children}</span>;
}

function getTaskStatusNotificationTone(status: unknown) {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');

  if (normalized === 'completed') {
    return 'success' as const;
  }

  if (normalized === 'inprogress') {
    return 'info' as const;
  }

  if (normalized === 'archived') {
    return 'warning' as const;
  }

  return 'default' as const;
}

function renderTaskNotification(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);
  const task = event.payload.task;

  if (event.type === 'task.assigned') {
    return (
      <>
        <strong>{actor}</strong> assigned you to{' '}
        <NotificationLink entity={task} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'task.unassigned') {
    return (
      <>
        <strong>{actor}</strong> removed you from{' '}
        <NotificationLink entity={task} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'task.status_changed' || event.type === 'task.reopened') {
    const status = event.payload.status ?? 'a new status';

    return (
      <>
        <strong>{actor}</strong> changed{' '}
        <NotificationLink entity={task} fallback={event.subject.label} /> to{' '}
        <NotificationValue tone={getTaskStatusNotificationTone(status)}>
          {status}
        </NotificationValue>
        .
      </>
    );
  }

  if (event.type === 'task.due_date_changed') {
    return (
      <>
        <strong>{actor}</strong> updated the due date for{' '}
        <NotificationLink entity={task} fallback={event.subject.label} />.
      </>
    );
  }

  return (
    <>
      <strong>{actor}</strong> updated{' '}
      <NotificationLink entity={task} fallback={event.subject.label} />.
    </>
  );
}

function renderChatMention(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);
  const conversation = event.payload.conversation;
  const message = event.payload.message;
  const messageText = message?.text ?? message?.body_text;

  return (
    <>
      <strong>{actor}</strong> mentioned you in{' '}
      <NotificationLink entity={conversation} fallback="a chat" />:
      {messageText ? (
        <>
          <br />
          <span className="notificationChatMessage">
            &quot;{messageText}&quot;
          </span>
        </>
      ) : null}
    </>
  );
}

function renderJobNotification(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);
  const job = event.payload.job;

  if (event.type === 'job.created') {
    return (
      <>
        <strong>{actor}</strong> created{' '}
        <NotificationLink entity={job} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'job.status_changed') {
    return (
      <>
        <strong>{actor}</strong> changed{' '}
        <NotificationLink entity={job} fallback={event.subject.label} /> to{' '}
        <NotificationValue tone="success">
          {event.payload.status ?? 'a new status'}
        </NotificationValue>
        .
      </>
    );
  }

  return (
    <>
      <strong>{actor}</strong> updated{' '}
      <NotificationLink entity={job} fallback={event.subject.label} />.
    </>
  );
}

function renderDeliverableNotification(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);

  return (
    <>
      <strong>{actor}</strong> updated{' '}
      <NotificationLink
        entity={event.payload.deliverable}
        fallback={event.subject.label}
      />
      .
    </>
  );
}

function renderChangeOrderNotification(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);

  return (
    <>
      <strong>{actor}</strong> updated change order{' '}
      <NotificationLink
        entity={event.payload.change_order}
        fallback={event.subject.label}
      />
      .
    </>
  );
}

function renderWorkspaceNotification(notification: NotificationInboxItem) {
  const { event } = notification;
  const actor = getActorName(notification);
  const memberName = event.payload.member?.user?.display_name;
  const requesterName = event.payload.requester?.display_name;
  const workspace = event.payload.workspace;

  if (event.type === 'workspace.invite.accepted') {
    return (
      <>
        <NotificationValue>{memberName ?? actor}</NotificationValue> accepted an
        invite to{' '}
        <NotificationLink entity={workspace} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'workspace.member.added') {
    const memberUserId = event.payload.member?.user?.id;
    const isActorMember = Boolean(
      memberUserId && event.actor?.id === memberUserId,
    );

    if (isActorMember) {
      return (
        <>
          <NotificationValue>{memberName ?? actor}</NotificationValue> joined{' '}
          <NotificationLink entity={workspace} fallback={event.subject.label} />
          .
        </>
      );
    }

    return (
      <>
        <strong>{actor}</strong> added{' '}
        <NotificationValue>{memberName ?? 'a member'}</NotificationValue> to{' '}
        <NotificationLink entity={workspace} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'workspace.join_request.created') {
    return (
      <>
        <NotificationValue>{requesterName ?? actor}</NotificationValue>{' '}
        requested to join{' '}
        <NotificationLink entity={workspace} fallback={event.subject.label} />.
      </>
    );
  }

  if (event.type === 'workspace.join_request.resubmitted') {
    return (
      <>
        <NotificationValue>{requesterName ?? actor}</NotificationValue>{' '}
        resubmitted a request to join{' '}
        <NotificationLink entity={workspace} fallback={event.subject.label} />.
      </>
    );
  }

  return (
    <>
      <strong>{actor}</strong> updated{' '}
      <NotificationLink entity={workspace} fallback={event.subject.label} />
      {memberName || requesterName ? (
        <>
          {' '}
          for{' '}
          <NotificationValue>{memberName ?? requesterName}</NotificationValue>
        </>
      ) : null}
      .
    </>
  );
}

function renderContactNotification(notification: NotificationInboxItem) {
  const { event } = notification;

  if (event.type === 'contact.import.failed') {
    return (
      <>
        Contact import{' '}
        <NotificationValue tone="error">failed</NotificationValue>
        {event.payload.error_message ? `: ${event.payload.error_message}` : '.'}
      </>
    );
  }

  if (event.type === 'contact.limit_reached') {
    return (
      <>
        Contact limit reached.{' '}
        <NotificationValue tone="warning">
          {event.payload.limit
            ? `${event.payload.limit} contacts`
            : 'Review plan'}
        </NotificationValue>
        .
      </>
    );
  }

  return <>Contact import updated.</>;
}

export function renderNotificationMessage(notification: NotificationInboxItem) {
  const type = notification.event.type;

  if (type === 'chat.mention.created') {
    return renderChatMention(notification);
  }

  if (type.startsWith('task.')) {
    return renderTaskNotification(notification);
  }

  if (type.startsWith('job.')) {
    return renderJobNotification(notification);
  }

  if (type.startsWith('deliverable.')) {
    return renderDeliverableNotification(notification);
  }

  if (type.startsWith('change_order.')) {
    return renderChangeOrderNotification(notification);
  }

  if (type.startsWith('workspace.')) {
    return renderWorkspaceNotification(notification);
  }

  if (type.startsWith('contact.')) {
    return renderContactNotification(notification);
  }

  return (
    <>
      <strong>{getActorName(notification)}</strong> updated{' '}
      <span>{notification.event.subject.label}</span>.
    </>
  );
}

export function getNotificationHref(notification: NotificationInboxItem) {
  const { payload, subject } = notification.event;
  const candidates = [
    payload.message?.url,
    payload.conversation?.url,
    payload.task?.url,
    payload.deliverable?.url,
    payload.job?.url,
    payload.change_order?.url,
    payload.workspace?.url,
  ];

  return (
    candidates.find(Boolean) ?? (subject.type === 'workspace' ? '/' : null)
  );
}
